import React, { useContext, Dispatch, SetStateAction, Suspense } from 'react';
import UrvalDialog from '../../../../../../../../../Components/Urval/Components/UrvalDialog/UrvalDialog';
import { KodplanContext } from '../../../../../../../../../Components/KodplanManager/Context/KodplanContext';
import RutinUrvalListQuery from '../../../../../../../../../Components/Urval/Components/SavedUrval/Queries/RutinUrvalListQuery/RutinUrvalListQuery';
import { ModelContext } from '../../../../../../../../../Model/ModelContext/ModelContext';
import { TEDSelectionVisibility } from '../../../../../../../../../globalQueryTypes';
import analysutdelningQuery from '../../../../Queries/AnalysutdelningQuery/AnalysutdelningQuery.gql';
import {
  AnalysutdelningQuery,
  AnalysutdelningQueryVariables,
  AnalysutdelningQuery_ek_grundurval,
} from '../../../../Queries/AnalysutdelningQuery/_QueryTypes/AnalysutdelningQuery';
import { ActiveDatabaseContext } from '../../../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { append, remove } from 'ramda';
import Logger from '../../../../../../../../../Utils/Logger';
import UrvalGQIDQuery from '../../../../../../../../../Components/Urval/Queries/UrvalGQIDQuery/UrvalGQIDQuery';
import NodeUrvalQuery from '../../../../../../../../../Components/Urval/Components/SavedUrval/Queries/NodeUrvalQuery/NodeUrvalQuery';
import { convertUrval } from '../../../../../../../../../Components/Urval/Components/SavedUrval/Utils/convertSavedUrval';
import {
  urvalFromKodplan,
  getEmptyUrval,
  conditionHasValue,
} from '../../../../../../../../../Components/Urval/Utils/urvalUtils';
import { ChangeUrvalFnType } from '../../../AnalysUtdelningMain/AnalysUtdelningMain';
import { IRapportUtdelning } from '../../../../Queries/AnalysutdelningQuery/AnalysutdelningQuery';
import mergeSort, {
  stringCompareNumeric,
} from '../../../../../../../../../Utils/mergeSort';

interface IProps {
  analys: IRapportUtdelning;
  changeUrval: ChangeUrvalFnType;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

/**
 * Urvaldsdialog för AnalysUtdelningsrutinen
 */
const AnalysUtdelningTableRowUrvalDialog: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const kodplanContext = useContext(KodplanContext);
  const modelContext = useContext(ModelContext);
  const activeDatabaseContext = useContext(ActiveDatabaseContext);

  return (
    <UrvalGQIDQuery
      sysUrvalId={
        props.analys.grundUrval
          ? props.analys.grundUrval.id || undefined
          : undefined
      }
    >
      {({ error, loading, data }) => {
        return (
          <NodeUrvalQuery
            id={
              data && data.node && data.node.__typename === 'TEDSYSUrval'
                ? data.node.SelectionGQID
                : undefined
            }
          >
            {({
              loading: nodeUrvalLoading,
              error: nodeUrvalError,
              urval: nodeUrval,
            }) => {
              if (error || loading || nodeUrvalError || nodeUrvalLoading) {
                return null;
              }

              const urval =
                props.analys.grundUrval && nodeUrval
                  ? convertUrval(
                      nodeUrval,
                      'EKKonto',
                      modelContext.avenyCollectionMap
                    )
                  : kodplanContext.activeKodplan
                  ? urvalFromKodplan(
                      kodplanContext.activeKodplan,
                      modelContext.avenyCollectionMap
                    )
                  : getEmptyUrval();

              const kontodelar = kodplanContext.activeKodplan
                ? urvalFromKodplan(
                    kodplanContext.activeKodplan,
                    modelContext.avenyCollectionMap
                  )
                : getEmptyUrval();

              if (urval.pages[0]) {
                const existingKontodelar =
                  urval.pages[0].conditionObjects.filter(o =>
                    o.id.startsWith('KONTODEL_')
                  );
                const existingNotKontodelar =
                  urval.pages[0].conditionObjects.filter(
                    o => !o.id.startsWith('KONTODEL_')
                  );

                const missingKontodelar =
                  kontodelar.pages[0].conditionObjects.filter(
                    k =>
                      !existingKontodelar.some(
                        e => e.id.slice(0, 11) === k.id.slice(0, 11)
                      )
                  );

                const kontodelarConditionObjects = mergeSort(
                  [...existingKontodelar, ...missingKontodelar],
                  (a, b) => {
                    return stringCompareNumeric(a.id, b.id);
                  }
                );
                urval.pages[0].conditionObjects = [
                  ...kontodelarConditionObjects,
                  ...existingNotKontodelar,
                ].filter(
                  co => co.id.startsWith('KONTODEL_') || conditionHasValue(co)
                );
              }

              return (
                <RutinUrvalListQuery
                  codeSystemID={
                    kodplanContext.activeKodplan
                      ? kodplanContext.activeKodplan.id
                      : undefined
                  }
                  collectionName={'EKKonto'}
                  routineID={0}
                  kodplanUrval={false}
                  usageCode={'EKLOP'}
                  showDBStandard
                >
                  {({ rutinUrvalList, variables }) => (
                    <Suspense fallback={null}>
                      <UrvalDialog
                        setUrval={newUrval => {
                          if (
                            newUrval.savedUrvalProps &&
                            newUrval.savedUrvalProps.urvalID
                          ) {
                            props.changeUrval(
                              props.analys,
                              newUrval.savedUrvalProps.urvalID
                            );
                          } else {
                            Logger.error(
                              Error(
                                'Tried to use unsaved urval as grundurval in analysutdelning'
                              )
                            );
                          }
                        }}
                        removeUrvalMutationUpdater={(
                          client,
                          removedUrvalID
                        ) => {
                          if (removedUrvalID) {
                            const data = client.readQuery<
                              AnalysutdelningQuery,
                              AnalysutdelningQueryVariables
                            >({
                              query: analysutdelningQuery,
                              variables: {
                                dbName: activeDatabaseContext.activeDatabase
                                  ? activeDatabaseContext.activeDatabase
                                  : '',
                              },
                            });

                            if (data && data.ek_grundurval) {
                              const removeIndex = data.ek_grundurval.findIndex(
                                urval => urval.id === removedUrvalID
                              );

                              if (removeIndex > -1) {
                                const newEkGrundurval = remove(
                                  removeIndex,
                                  1,
                                  data.ek_grundurval
                                );

                                const newData = {
                                  ...data,
                                  ek_grundurval: newEkGrundurval,
                                };

                                client.writeQuery<
                                  AnalysutdelningQuery,
                                  AnalysutdelningQueryVariables
                                >({
                                  query: analysutdelningQuery,
                                  data: newData,
                                  variables: {
                                    dbName: activeDatabaseContext.activeDatabase
                                      ? activeDatabaseContext.activeDatabase
                                      : '',
                                  },
                                });
                              }
                            }
                          }
                        }}
                        saveNewUrvalMutationUpdater={(client, result) => {
                          // Lägg till det nya urvalet i cachen så att det dyker upp i grundurval select
                          if (
                            result.data &&
                            result.data.saveselection &&
                            result.data.saveselection.urvalID
                          ) {
                            const data = client.readQuery<
                              AnalysutdelningQuery,
                              AnalysutdelningQueryVariables
                            >({
                              query: analysutdelningQuery,
                              variables: {
                                dbName: activeDatabaseContext.activeDatabase
                                  ? activeDatabaseContext.activeDatabase
                                  : '',
                              },
                            });

                            if (data && data.ek_grundurval) {
                              const newEkGrundurval =
                                append<AnalysutdelningQuery_ek_grundurval>(
                                  {
                                    NAME: result.data.saveselection.namn,
                                    id: result.data.saveselection.urvalID,
                                    VISIBILITY: null,
                                    __typename: 'TEDSYSUrval',
                                  },
                                  data.ek_grundurval
                                );

                              const newData = {
                                ...data,
                                ek_grundurval: newEkGrundurval,
                              };

                              client.writeQuery<
                                AnalysutdelningQuery,
                                AnalysutdelningQueryVariables
                              >({
                                query: analysutdelningQuery,
                                data: newData,
                                variables: {
                                  dbName: activeDatabaseContext.activeDatabase
                                    ? activeDatabaseContext.activeDatabase
                                    : '',
                                },
                              });
                            }
                          }
                        }}
                        collectionName={'EKKonto'}
                        rutinUrvalList={rutinUrvalList}
                        rutinUrvalVariables={variables}
                        routineID={0}
                        usageCode={'EKLOP'}
                        codeSystemID={
                          kodplanContext.activeKodplan
                            ? kodplanContext.activeKodplan.id
                            : undefined
                        }
                        kodbehorighet={
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          />
                        }
                        urval={urval}
                        open={props.open}
                        onClose={() => {
                          props.setOpen(false);
                        }}
                        onCommit={() => {
                          props.setOpen(false);
                        }}
                        openFieldPickerWithAllExpandableOpen={true}
                        fieldSortMode="CustomSortOrder"
                        kodplanUrval={false}
                        saveUrvalAvailableVisibilityOptions={[
                          TEDSelectionVisibility.gemensamt,
                        ]}
                        forceSave={true}
                        hideUrvalHitIndicator
                      />
                    </Suspense>
                  )}
                </RutinUrvalListQuery>
              );
            }}
          </NodeUrvalQuery>
        );
      }}
    </UrvalGQIDQuery>
  );
};

export default AnalysUtdelningTableRowUrvalDialog;
